/**
 * JoinPage (WU-19, RolesUsersPlan §4.8) — join a company or project by link
 * (/p/join/<token>). Copies InvitePage's load-state union and claim form, but
 * creates a USER only (never a company) and never signs the browser in: the
 * claim omits `signIn`, and success ends on "Open the app".
 *
 * The preview card, claim form, success and no-longer-valid screens are
 * exported for JoinCodePage (/p/join), which reaches the same form by code.
 *
 * "Continue with Google" sits above the password fields when Google sign-in is
 * configured: /auth/google/check verifies the person WITHOUT signing the browser
 * in, and the claim then uses the signed identity instead of a password.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { BRAND, JoinClaimRequestSchema, ROLE_LABELS } from '@buildledger/shared';
import type { JoinClaimResponse, JoinPreview } from '@buildledger/shared';

import { fmtDate } from '../format';
import { GoogleButton } from '../GoogleButton';
import {
  getJoinPreview,
  isUnusableError,
  joinErrorMessage,
  postGoogleCheck,
  postJoinClaim,
  postJoinClaimWithGoogle,
} from '../join-api';
import type { JoinClaimBody } from '../join-api';
import '../join.css';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; unusable: boolean; message: string }
  | { kind: 'ready'; preview: JoinPreview };

type Via = 'link' | 'code';

export function JoinPage({ token }: { token: string }): JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [gone, setGone] = useState(false);
  const [claimed, setClaimed] = useState<{ result: JoinClaimResponse; viaGoogle: boolean } | null>(
    null,
  );

  useEffect(() => {
    getJoinPreview(token)
      .then((preview) => setLoad({ kind: 'ready', preview }))
      .catch((err: unknown) =>
        setLoad({
          kind: 'error',
          unusable: isUnusableError(err, 'link'),
          message: joinErrorMessage(err, 'link'),
        }),
      );
  }, [token]);

  if (load.kind === 'loading') {
    return (
      <div className="stage stage-center" data-testid="join-page">
        <div className="spinner" role="status" aria-label="Loading invite" />
      </div>
    );
  }

  if (load.kind === 'error' && !load.unusable) {
    return (
      <div className="stage stage-center" data-testid="join-page">
        <div className="card notice-card">
          <div className="brand-rule" />
          <h1>Something went wrong</h1>
          <p role="alert">{load.message}</p>
        </div>
      </div>
    );
  }

  if (load.kind === 'error' || gone || !load.preview.usable) {
    return <JoinUnusable via="link" />;
  }

  if (claimed !== null) {
    return <JoinSuccess result={claimed.result} viaGoogle={claimed.viaGoogle} />;
  }

  return (
    <div className="stage stage-center" data-testid="join-page">
      <div className="card invite-card">
        <JoinPreviewHeader preview={load.preview} />
        <JoinClaimForm
          via="link"
          submit={(body) => postJoinClaim(token, body)}
          submitGoogle={(signupToken, name) => postJoinClaimWithGoogle(token, signupToken, name)}
          onClaimed={(result, viaGoogle) => setClaimed({ result, viaGoogle })}
          onGone={() => setGone(true)}
        />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ shared

/** "Join {company}" + project / crew / role / expiry. Render inside a card. */
export function JoinPreviewHeader({ preview }: { preview: JoinPreview }): JSX.Element {
  return (
    <>
      <div className="brand-rule" />
      <p className="invite-kicker">Invitation to join</p>
      <h1 data-testid="join-company">Join {preview.companyName}</h1>
      <dl className="join-facts">
        {preview.projectName !== null && (
          <div>
            <dt>Project</dt>
            <dd>{preview.projectName}</dd>
          </div>
        )}
        {preview.crewName !== null && (
          <div>
            <dt>Crew</dt>
            <dd>{preview.crewName}</dd>
          </div>
        )}
        <div>
          <dt>Role</dt>
          <dd>{ROLE_LABELS[preview.roleDefault]}</dd>
        </div>
        <div>
          <dt>Invite expires</dt>
          <dd>{fmtDate(preview.expiresAt)}</dd>
        </div>
      </dl>
    </>
  );
}

/** Name / email / password → create the login. Never signs the browser in. */
export function JoinClaimForm({
  via,
  submit,
  submitGoogle,
  onClaimed,
  onGone,
}: {
  via: Via;
  submit: (body: JoinClaimBody) => Promise<JoinClaimResponse>;
  /** Claim with a Google identity (the signed token from /auth/google/check). */
  submitGoogle: (signupToken: string, name?: string) => Promise<JoinClaimResponse>;
  onClaimed: (result: JoinClaimResponse, viaGoogle: boolean) => void;
  onGone: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A verified Google identity that Google gave no name for — ask for one. */
  const [googlePending, setGooglePending] = useState<{ signupToken: string; email: string } | null>(
    null,
  );

  const claimWithGoogle = (signupToken: string, claimName?: string): void => {
    setBusy(true);
    setError(null);
    submitGoogle(signupToken, claimName)
      .then((result) => onClaimed(result, true))
      .catch((err: unknown) => {
        if (isUnusableError(err, via)) onGone();
        else setError(joinErrorMessage(err, via));
      })
      .finally(() => setBusy(false));
  };

  const onGoogle = (idToken: string): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    postGoogleCheck(idToken)
      .then((res) => {
        if (res.status === 'account_exists') {
          setBusy(false);
          setError(
            `${res.email} already has a ${BRAND.name} login. Open the app, continue with Google, ` +
              'then go to Settings → Join with a code.',
          );
        } else if (res.name) {
          claimWithGoogle(res.signupToken);
        } else {
          setBusy(false);
          setGooglePending({ signupToken: res.signupToken, email: res.email });
        }
      })
      .catch((err: unknown) => {
        setBusy(false);
        setError(joinErrorMessage(err, via));
      });
  };

  if (googlePending !== null) {
    const trimmed = name.trim();
    return (
      <form
        className="invite-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed.length === 0 || busy) return;
          claimWithGoogle(googlePending.signupToken, trimmed);
        }}
      >
        <h3>Almost there</h3>
        <p className="muted">
          Joining with Google as <strong>{googlePending.email}</strong>.
        </p>
        <label className="field">
          <span>Your name</span>
          <input
            value={name}
            data-testid="join-name"
            autoComplete="name"
            required
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {error !== null && (
          <p className="form-error" role="alert" data-testid="join-error">
            {error}
          </p>
        )}
        <button
          className="btn btn-primary btn-wide"
          type="submit"
          data-testid="join-submit"
          disabled={trimmed.length === 0 || busy}
        >
          {busy ? 'Joining…' : 'Join'}
        </button>
      </form>
    );
  }

  const parsed = useMemo(
    () => JoinClaimRequestSchema.safeParse({ name: name.trim(), email: email.trim(), password }),
    [name, email, password],
  );

  return (
    <form
      className="invite-form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (!parsed.success || busy) return;
        setBusy(true);
        setError(null);
        const { name: n, email: em, password: pw } = parsed.data;
        submit({ name: n, email: em, password: pw })
          .then((result) => onClaimed(result, false))
          .catch((err: unknown) => {
            if (isUnusableError(err, via)) onGone();
            else setError(joinErrorMessage(err, via));
          })
          .finally(() => setBusy(false));
      }}
    >
      <h3>Create your login</h3>
      <GoogleButton onCredential={onGoogle} />
      <label className="field">
        <span>Your name</span>
        <input
          value={name}
          data-testid="join-name"
          autoComplete="name"
          required
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="field">
        <span>Email</span>
        <input
          value={email}
          data-testid="join-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <label className="field">
        <span>Password (10+ characters)</span>
        <input
          value={password}
          data-testid="join-password"
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          onChange={(e) => setPassword(e.target.value)}
        />
      </label>
      {error !== null && (
        <p className="form-error" role="alert" data-testid="join-error">
          {error}
        </p>
      )}
      <button
        className="btn btn-primary btn-wide"
        type="submit"
        data-testid="join-submit"
        disabled={!parsed.success || busy}
      >
        {busy ? 'Joining…' : 'Create login & join'}
      </button>
      <p className="muted invite-fineprint">
        This creates your {BRAND.name} login. You&apos;ll sign in from the {BRAND.name} app — this
        page never signs you in.
      </p>
    </form>
  );
}

/** "You're in." — open the app and sign in (store names only, no links). */
export function JoinSuccess({
  result,
  viaGoogle = false,
}: {
  result: JoinClaimResponse;
  /** Joined through Google: they sign in to the app with Google, not a password. */
  viaGoogle?: boolean;
}): JSX.Element {
  const where = [
    result.projectName !== null ? `project ${result.projectName}` : null,
    result.crewName !== null ? `crew ${result.crewName}` : null,
  ].filter((part): part is string => part !== null);
  return (
    <div className="stage stage-center">
      <div className="card notice-card" data-testid="join-success">
        <div className="brand-rule" />
        <h1>You&apos;re in.</h1>
        <p>
          {viaGoogle ? (
            <>
              Open the {BRAND.name} app and tap <strong>Continue with Google</strong> as{' '}
              <strong>{result.user.email}</strong>.
            </>
          ) : (
            <>
              Open the {BRAND.name} app and sign in with <strong>{result.user.email}</strong>.
            </>
          )}
        </p>
        <p className="muted">
          You joined {result.companyName}
          {where.length > 0 ? ` (${where.join(', ')})` : ''}.
        </p>
        <ul className="join-stores" aria-label={`Get the ${BRAND.name} app`}>
          <li className="join-store">
            <strong>iPhone</strong>
            <span>Search “{BRAND.name}” on the App Store</span>
          </li>
          <li className="join-store">
            <strong>Android</strong>
            <span>Search “{BRAND.name}” on Google Play</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

/** Revoked, expired, exhausted (or unknown link). */
export function JoinUnusable({ via, onRetry }: { via: Via; onRetry?: () => void }): JSX.Element {
  return (
    <div className="stage stage-center">
      <div className="card notice-card" data-testid="join-unusable">
        <div className="brand-rule" />
        <h1>This invite is no longer valid.</h1>
        <p>{via === 'link' ? 'Ask for a new link.' : 'Ask for a new code.'}</p>
        {onRetry !== undefined && (
          <button className="btn btn-ghost" type="button" onClick={onRetry}>
            Enter a different code
          </button>
        )}
      </div>
    </div>
  );
}
